import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { BackendErrorCode, getErrorReason, toApiError } from 'shared/errors';

/** Статус ошибки, когда картинку поста не удалось скачать из хранилища до отправки. */
export const IMAGE_DOWNLOAD_ERROR_STATUS = 'IMAGE_DOWNLOAD';

/** Ограничения `POST /tournament-integrations/{id}/posts`. */
export const POST_TEXT_MAX_LENGTH = 4096;
export const POST_IMAGES_MAX_COUNT = 10;

/**
 * REST `POST /tournament-integrations/{id}/posts` отказывает HTTP-статусом, а не кодом бэкенда —
 * у статусов смысл именно этого эндпойнта, поэтому их тексты живут здесь, а не в общем реестре.
 * GraphQL-отказы интеграций (`24400`/`24404`/`24409`/`24422`) переводит общий реестр `shared/errors`.
 */
const MESSAGES_BY_REST_STATUS: Record<number, MessageDescriptor> = {
  400: msg`Пост не прошёл проверку: нужен текст до 4096 символов или картинка (до 10 файлов jpeg, png или webp).`,
  403: msg`Недостаточно прав: интеграциями управляет администратор организации.`,
  404: msg`Подключение не найдено — возможно, его уже удалили. Обновите страницу.`,
  409: msg`Бот потерял доступ к каналу. Добавьте его в администраторы канала снова или переподключите канал в настройках.`,
  413: msg`Пост больше 5 МБ — Telegram его не примет.`,
  502: msg`Telegram отклонил пост или сейчас недоступен. Попробуйте позже.`,
};

const IMAGE_DOWNLOAD_MESSAGE = msg`Не удалось загрузить картинку для поста. Попробуйте ещё раз.`;

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

/**
 * Понятное пользователю сообщение об ошибке интеграции; `fallback` — когда перевода нет.
 *
 * Порядок: картинка не скачалась → причина из общего реестра (код GraphQL, нет сети) → HTTP-статус
 * REST. Статус GraphQL-отказа (`400` от `graphqlBaseQuery`) по таблице REST не читается.
 */
export function getIntegrationErrorMessage(error: unknown, fallback: MessageDescriptor): MessageDescriptor {
  const { status, data } = (error && typeof error === 'object' ? error : {}) as ErrorLike;
  if (status === IMAGE_DOWNLOAD_ERROR_STATUS) return IMAGE_DOWNLOAD_MESSAGE;

  // 403 GraphQL-отказа здесь точнее общего «Недостаточно прав»: называет, кто управляет интеграциями.
  const reason = getErrorReason(error, { overrides: { [BackendErrorCode.NOT_ALLOWED]: MESSAGES_BY_REST_STATUS[403] } });
  if (reason) return reason;

  const isGraphqlRefusal = Array.isArray(data);
  const restStatus = toApiError(error).status;
  if (!isGraphqlRefusal && restStatus !== undefined && MESSAGES_BY_REST_STATUS[restStatus]) return MESSAGES_BY_REST_STATUS[restStatus];

  return fallback;
}
